/**
 * Pure-function utilities for the Events calendar (EventsMonth).
 *
 * Extracted so they can be unit-tested independently of Mobiscroll / React.
 */

import momentTZ from 'moment-timezone';

type DateInput = string | Date | null | undefined;

/**
 * Normalizes API time strings (e.g. "18:0", "20:0") to "HH:mm:ss".
 */
export const normalizeTimeStr = (timeStr?: string | null): string => {
	if (!timeStr) return '00:00:00';

	const parts = timeStr.trim().split(':');
	if (parts.length === 0 || parts[0] === '') return '00:00:00';

	const hours = Number(parts[0]);
	const minutes = Number(parts[1] ?? '0');
	const seconds = Number(parts[2] ?? '0');

	if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) {
		return '00:00:00';
	}

	const pad = (value: number) => String(value).padStart(2, '0');
	return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

/**
 * Builds a local datetime string ("YYYY-MM-DDTHH:mm:ss") from a date + time pair
 * using moment — no `new Date()`, no timezone conversion.
 *
 * Passing a plain string to Mobiscroll's Eventcalendar with `dataTimezone` set
 * causes Mobiscroll to parse the string directly in the workplace timezone,
 * bypassing the UTC re-interpretation that occurs when a JS Date object is used.
 *
 * @param dateStr  - ISO date/datetime string treated as local time (e.g. "2026-05-28T09:00:00")
 * @param timeStr  - Optional HH:mm:ss time override (e.g. "09:00:00")
 * @returns        "YYYY-MM-DDTHH:mm:ss" string, or null when dateStr is absent/invalid
 */
export const buildDateTimeStr = (dateStr: DateInput, timeStr?: string | null): string | null => {
	if (!dateStr) return null;
	const parsed = momentTZ(dateStr);
	if (!parsed.isValid()) return null;
	const datePart = parsed.format('YYYY-MM-DD');
	const time = normalizeTimeStr(timeStr);
	return `${datePart}T${time}`;
};

/**
 * Maps raw API event objects to the shape expected by Mobiscroll's Eventcalendar.
 *
 * `start` and `end` are plain strings so that Mobiscroll interprets them directly
 * in the configured `dataTimezone` instead of re-converting a Date object's UTC value.
 */
export const mapApiEvents = (items: any[]): { id: any; start: string; end: string; title: any; name: any; originalEvent: any; background_color: string; text_color: string }[] =>
	items.map((event: any) => {
		const start =
			buildDateTimeStr(event.startDate, event.startTime) ||
			buildDateTimeStr(event.start, event.startTime) ||
			momentTZ().format('YYYY-MM-DDTHH:mm:ss');
		const end =
			buildDateTimeStr(event.endDate, event.endTime) ||
			buildDateTimeStr(event.end, event.endTime) ||
			momentTZ().format('YYYY-MM-DDTHH:mm:ss');

		return {
			id: event.id,
			start,
			end,
			title: event.name,
			name: event.name,
			originalEvent: event,
			background_color: 'rgb(68, 99, 136)',
			text_color: 'white',
		};
	});
